import { create } from 'zustand';
import { Profile, Tobacco, CheckIn, BadgeDefinition, Venue } from './supabase';
import { setSentryUserLazy } from './sentryClient';

interface CheckInDraftState {
  locationName?: string;
  tobaccoInput?: string;
  selectedCellarTobaccoId?: string | null;
  tobaccoManual?: string;
  rating?: number;
  notes?: string;
  pipeInput?: string;
  selectedCellarPipeId?: string | null;
  pipeManual?: string;
  drinkPairing?: string;
  lighterInput?: string;
  selectedCellarLighterId?: string | null;
  lighterManual?: string;
  outdoorSeating?: boolean;
  coveredSeating?: boolean;
  heaters?: boolean;
  isPublic?: boolean;
  selectedVenueId?: string | null;
  photoPreview?: string | null;
  photoFile?: File | null;
}

interface EmberStore {
  // User
  currentUser: Profile | null;
  setCurrentUser: (user: Profile | null) => void;
  updateUserProfile: (user: Profile) => void;

  // Tobaccos
  tobaccos: Tobacco[];
  setTobaccos: (tobaccos: Tobacco[]) => void;

  // Check-ins
  checkIns: CheckIn[];
  setCheckIns: (checkIns: CheckIn[]) => void;
  addCheckIn: (checkIn: CheckIn) => void;

  // Badges
  badges: BadgeDefinition[];
  setBadges: (badges: BadgeDefinition[]) => void;

  // Venues
  venues: Venue[];
  setVenues: (venues: Venue[]) => void;

  // UI State
  isLoading: boolean;
  setIsLoading: (loading: boolean) => void;

  // Check-in draft (persist across component remounts)
  checkInDraft: CheckInDraftState | null;
  setCheckInDraft: (draft: CheckInDraftState | null | ((prev: CheckInDraftState | null) => CheckInDraftState | null)) => void;
  clearCheckInDraft: () => void;
}

export const useEmberStore = create<EmberStore>((set) => ({
  // User
  currentUser: null,
  setCurrentUser: (user) => {
    set({ currentUser: user });
    
    // Set Sentry user context
    if (user) {
      setSentryUserLazy({
        id: user.id,
        username: user.username
      });
    } else {
      setSentryUserLazy(null);
    }
  },
  updateUserProfile: (user) => {
    set({ currentUser: user });
    
    // Update Sentry user context
    if (user) {
      setSentryUserLazy({
        id: user.id,
        username: user.username
      });
    }
  },

  // Tobaccos
  tobaccos: [],
  setTobaccos: (tobaccos) => set({ tobaccos }),

  // Check-ins
  checkIns: [],
  setCheckIns: (checkIns) => set({ checkIns }),
  addCheckIn: (checkIn) => set((state) => ({ checkIns: [checkIn, ...state.checkIns] })),

  // Badges
  badges: [],
  setBadges: (badges) => set({ badges }),

  // Venues
  venues: [],
  setVenues: (venues) => set({ venues }),

  // UI State
  isLoading: false,
  setIsLoading: (loading) => set({ isLoading: loading }),

  // Check-in draft
  checkInDraft: null,
  setCheckInDraft: (draft) =>
    set((state) => {
      const nextDraft = typeof draft === 'function' ? draft(state.checkInDraft) : draft;
      return { checkInDraft: nextDraft };
    }),
  clearCheckInDraft: () => set({ checkInDraft: null }),
}));

